import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Chip, StatCard, LoadingState, CardLoadingSkeleton } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash, PencilSimple, MagnifyingGlass, FileXls, Car } from "@phosphor-icons/react";
import { toast } from "sonner";
import FlatsMasterImport from "@/components/FlatsMasterImport";
import FlatEditDialog from "@/components/FlatEditDialog";

const FLAT_TYPES = ["1RK", "1BHK", "2BHK", "2.5BHK", "3BHK", "4BHK", "5BHK", "6BHK"];

export default function Flats() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [flats, setFlats] = useState([]);
    const [summary, setSummary] = useState(null);
    const [open, setOpen] = useState(false);
    const [masterImportOpen, setMasterImportOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [blockFilter, setBlockFilter] = useState("all");
    const [occupancyFilter, setOccupancyFilter] = useState("all");
    const [flatTypeFilter, setFlatTypeFilter] = useState("all");
    const [parkingFilter, setParkingFilter] = useState("all");
    const [searchQuery, setSearchQuery] = useState("");
    const [loading, setLoading] = useState(true);

    async function load() {
        setLoading(true);
        try {
            const [f, s] = await Promise.all([api.get("/flats"), api.get("/flats/summary")]);
            setFlats(f.data);
            setSummary(s.data);
        } catch (e) { toast.error(formatError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    const isStaff = user?.role === "admin" || user?.role === "committee";

    function openCreate() {
        setEditing(null);
        setOpen(true);
    }
    function openEdit(f) {
        setEditing(f);
        setOpen(true);
    }
    async function remove(f) {
        if (!confirm(`Delete flat ${f.block}-${f.number}?`)) return;
        try { await api.delete(`/flats/${f.id}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error(formatError(e)); }
    }
    function clearFilters() {
        setSearchQuery(""); setBlockFilter("all"); setOccupancyFilter("all"); setFlatTypeFilter("all"); setParkingFilter("all");
    }

    const blocks = useMemo(() => {
        const set = new Set(flats.map(f => f.block).filter(Boolean));
        return Array.from(set).sort();
    }, [flats]);

    const filtered = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        return flats.filter(f => {
            if (blockFilter !== "all" && f.block !== blockFilter) return false;
            if (occupancyFilter !== "all" && f.occupancy !== occupancyFilter) return false;
            if (flatTypeFilter !== "all" && f.bhk !== flatTypeFilter) return false;
            if (parkingFilter === "allocated" && !f.parking?.allotted) return false;
            if (parkingFilter === "none" && f.parking?.allotted) return false;
            if (!query) return true;
            const flatLabel = `${f.block}-${f.number}`.toLowerCase();
            const floor = (f.floor || "").toLowerCase();
            const bhk = (f.bhk || "").toLowerCase();
            return flatLabel.includes(query) || floor.includes(query) || bhk.includes(query) || (f.block || "").toLowerCase().includes(query);
        });
    }, [flats, blockFilter, occupancyFilter, flatTypeFilter, parkingFilter, searchQuery]);

    const grouped = useMemo(() => {
        const g = {};
        for (const f of filtered) {
            const k = f.block || "—";
            if (!g[k]) g[k] = [];
            g[k].push(f);
        }
        return Object.entries(g).sort(([a], [b]) => a.localeCompare(b));
    }, [filtered]);

    return (
        <div className="animate-fade-up">
            <PageHeader
                overline="Directory"
                title="Flats"
                description="Every unit in your society — grouped by block, with occupancy at a glance."
                actions={isStaff && (
                    <div className="flex flex-wrap gap-2">
                        <Button data-testid="master-import-btn" onClick={() => setMasterImportOpen(true)} variant="outline"
                            className="rounded-full border-brand-action text-brand-action hover:bg-brand-action hover:text-white">
                            <FileXls size={16} className="mr-1.5" /> Master Onboarding
                        </Button>
                        <Button data-testid="add-flat-btn" onClick={openCreate} className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white active:scale-[0.98]">
                            <Plus size={16} className="mr-1.5" /> Add flat
                        </Button>
                    </div>
                )}
            />

            {/* Summary */}
            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                    <StatCard label="Total flats" value={summary.total} hint={`${summary.blocks.length} blocks`} />
                    <StatCard label="Occupied" value={summary.occupied} hint={summary.total ? `${Math.round((summary.occupied/summary.total)*100)}%` : "0%"} />
                    <StatCard label="Vacant" value={summary.vacant} hint={summary.total ? `${Math.round((summary.vacant/summary.total)*100)}%` : "0%"} accent />
                    <StatCard label="Owner-occupied" value={summary.owners} />
                    <StatCard label="Tenant-occupied" value={summary.tenants} />
                </div>
            )}

            {/* Filters */}
            {flats.length > 0 && (
                <div className="bg-white border border-brand-line rounded-sm p-4 mb-6" data-testid="flat-filters">
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                        <div className="md:col-span-2 relative">
                            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-inkSoft" />
                            <Input
                                data-testid="flat-search-input"
                                type="text"
                                placeholder="Search by flat number, block, floor, or type..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="rounded-sm border-brand-line pl-9"
                            />
                        </div>
                        <Select value={blockFilter} onValueChange={setBlockFilter}>
                            <SelectTrigger data-testid="flat-block-filter" className="rounded-sm border-brand-line"><SelectValue placeholder="Block" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All blocks</SelectItem>
                                {blocks.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Select value={occupancyFilter} onValueChange={setOccupancyFilter}>
                            <SelectTrigger data-testid="flat-occupancy-filter" className="rounded-sm border-brand-line"><SelectValue placeholder="Occupancy" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All occupancy</SelectItem>
                                <SelectItem value="owner">Owner Occupied</SelectItem>
                                <SelectItem value="tenant">Tenant Occupied</SelectItem>
                                <SelectItem value="vacant">Vacant</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={flatTypeFilter} onValueChange={setFlatTypeFilter}>
                            <SelectTrigger data-testid="flat-type-filter" className="rounded-sm border-brand-line"><SelectValue placeholder="Flat Type" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All flat types</SelectItem>
                                {FLAT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Select value={parkingFilter} onValueChange={setParkingFilter}>
                            <SelectTrigger data-testid="flat-parking-filter" className="rounded-sm border-brand-line"><SelectValue placeholder="Parking" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All parking</SelectItem>
                                <SelectItem value="allocated">Parking Allocated</SelectItem>
                                <SelectItem value="none">No Parking</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="mt-3 flex justify-end">
                        <Button data-testid="flat-filters-clear" variant="ghost" size="sm" onClick={clearFilters} className="text-brand-inkSoft">Clear filters</Button>
                    </div>
                </div>
            )}

            {loading ? (
                <CardLoadingSkeleton count={6} />
            ) : flats.length === 0 ? (
                <EmptyState title="No flats yet" description="Add flats to start managing your society." />
            ) : filtered.length === 0 ? (
                <EmptyState title="No matches" description="Try a different filter." />
            ) : (
                <div className="space-y-8">
                    {grouped.map(([block, items]) => (
                        <section key={block} data-testid={`block-section-${block}`}>
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-8 h-8 rounded-sm bg-brand-ink text-white flex items-center justify-center font-heading">
                                    {block}
                                </div>
                                <h2 className="font-heading text-xl text-brand-ink">Block {block}</h2>
                                <span className="text-xs text-brand-inkSoft">— {items.length} flat{items.length !== 1 ? "s" : ""}</span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {items.map((f) => (
                                    <div key={f.id} data-testid={`flat-card-${f.id}`} onClick={() => navigate(`/app/flats/${f.id}`)}
                                        className="bg-white border border-brand-line rounded-sm p-6 hover:-translate-y-1 hover:shadow-lg transition-transform duration-200 cursor-pointer">
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Block {f.block}</p>
                                                <p className="font-heading text-3xl tracking-tight text-brand-ink mt-1">{f.number}</p>
                                                <p className="text-xs text-brand-inkSoft mt-1">{f.floor ? `Floor ${f.floor}` : ""} {f.bhk && `· ${f.bhk}`}</p>
                                            </div>
                                            <Chip variant={f.occupancy === "vacant" ? "neutral" : f.occupancy === "owner" ? "success" : "default"}>{f.occupancy}</Chip>
                                        </div>
                                        <div className="mt-3">
                                            <Chip data-testid={`flat-parking-${f.id}`} variant={f.parking?.allotted ? "success" : "neutral"}>
                                                <Car size={11} className="mr-1" />
                                                {f.parking?.allotted ? `Parking · ${f.parking.slot_number}` : "No parking"}
                                            </Chip>
                                        </div>
                                        <div className="mt-4 pt-4 border-t border-brand-line">
                                            <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft mb-2">Residents</p>
                                            {f.residents && f.residents.length > 0 ? (
                                                <div className="space-y-1">
                                                    {f.residents.map((r) => (
                                                        <div key={r.id} className="text-sm text-brand-ink">
                                                            {r.name} <span className="text-brand-inkSoft text-xs">· {r.role}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : <p className="text-sm text-brand-inkSoft">No residents assigned</p>}
                                        </div>
                                        {isStaff && (
                                            <div className="mt-4 flex justify-end gap-1">
                                                <button data-testid={`edit-flat-${f.id}`} onClick={(e) => { e.stopPropagation(); openEdit(f); }} className="p-1.5 hover:bg-brand-sage rounded-sm"><PencilSimple size={14} /></button>
                                                <button data-testid={`delete-flat-${f.id}`} onClick={(e) => { e.stopPropagation(); remove(f); }} className="p-1.5 hover:bg-[#FEF2F2] rounded-sm text-brand-action"><Trash size={14} /></button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            )}

            <FlatEditDialog open={open} onOpenChange={setOpen} editing={editing} onSaved={load} />

            <FlatsMasterImport
                open={masterImportOpen}
                onOpenChange={setMasterImportOpen}
                onDone={load}
            />
        </div>
    );
}
